import React, { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { Button, Table, notification } from '@surecompanies/core_components'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  DownloadOutlined,
  UploadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import Page from 'layouts/Page'
import BreadcrumbNav from '../../../../components/Breadcrumbs'
import { HRIS_PATH } from '../../constants'
import RowSection from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/HrisPayroll/component/RowSection'
import useAssistedPayroll, {
  AssistedPayrollSyncResult,
  AssistedPayrollSyncResultListItem,
} from './hooks/useAssistedPayroll'
import dayjs, { convertFromISODate, DateTimeFormats } from 'helpers/datetime'
import { TriggerPayrollSchema } from './trigger-payroll.schema'
import RHFRangePicker from 'enrollment-platform/components/form/hookform/RangePicker/RangePicker'
import RHFSelect from 'enrollment-platform/components/form/hookform/Select/RHFSelect'
import PayrollDetailModal from './PayrollDetailModal'
import UploadResultModal from './UploadResultModal'
import ClientSelect from 'components/ClientSelect'
import { PayrollCompanyListOptionsDto } from '../../api/dto/payroll-company-select-options.dto'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
type FormData = z.infer<typeof TriggerPayrollSchema>

export default function Payroll() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
  const { allowed: canManageUsRestrictedData } = usePermission(
    RbacActions.Manage,
    RbacSubjects.UsRestrictedData
  )

  const {
    data,
    total,
    isPending,
    triggerPayrollSync,
    isCreatingPayrollSync,
    companyList,
    companyMap,
    onViewDetails,
    isDetailsModalOpen,
    closeDetailsModal,
    getPayrollDownload,
    isUploadResultModalOpen,
    closeUploadResultModal,
    selectedPayrollId,
    openUploadResultModal,
    processPayroll,
    isProcessing,
  } = useAssistedPayroll({
    page,
    limit: pageSize,
    clientPublicKey: selectedCompanyId,
  })

  useEffect(() => {
    setPage(1)
  }, [selectedCompanyId])

  const { handleSubmit, control, reset } = useForm<FormData>({
    resolver: zodResolver(TriggerPayrollSchema),
    defaultValues: {
      payrollDates: [
        dayjs().startOf('year').toDate(),
        dayjs().endOf('year').toDate(),
      ],
    },
  })

  const companiesForSelect = useMemo(
    () =>
      (companyList ?? []).map((opt: PayrollCompanyListOptionsDto) => ({
        id: opt.value,
        companyName: opt.label,
      })),
    [companyList]
  )

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
    },
    {
      title: 'Customer',
      dataIndex: 'clientPublicKey',
      render: (key: string) => {
        return companyMap[key]?.companyName ?? 'N/A'
      },
    },
    {
      title: 'BER',
      dataIndex: 'rowCount',
      render: (rowCount: number) => (
        <Typography size={14} color={TextColors.secondary}>
          {rowCount} rows
        </Typography>
      ),
    },
    {
      title: 'Date Range',
      dataIndex: 'dateRange',
      render: (_, row: AssistedPayrollSyncResultListItem) =>
        `${convertFromISODate(row?.dateRangeStart, DateTimeFormats.a, DateTimeFormats.b)} - ${convertFromISODate(row?.dateRangeEnd, DateTimeFormats.a, DateTimeFormats.b)}`,
    },
    {
      title: 'Started By',
      dataIndex: 'startedByName',
    },
    {
      title: 'Output',
      dataIndex: 'assistedPayrollSyncResult',
      render: (payrollResult: AssistedPayrollSyncResult) => (
        <Typography size={14} as="a" color={TextColors.secondary}>
          {payrollResult.fileContent && payrollResult.status === 'COMPLETED'
            ? `${payrollResult.fileContent?.length} rows`
            : payrollResult.status}
        </Typography>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, row: AssistedPayrollSyncResultListItem) => (
        <Stack direction="row" space={10}>
          <Button
            variant="solid"
            color="primary"
            onClick={() => {
              onViewDetails(row.id)
            }}
          >
            View
          </Button>
          {row.assistedPayrollSyncResult.status === 'COMPLETED' && (
            <Button
              type="primary"
              color="primary"
              icon={<DownloadOutlined />}
              disabled={!canManageUsRestrictedData}
              onClick={async () => {
                try {
                  const companyName =
                    companyMap[row.clientPublicKey]?.companyName ??
                    'Unknown-Company'
                  const completedAt =
                    row.assistedPayrollSyncResult?.completedAt ?? new Date()
                  const date = dayjs(completedAt).format('YYYY-MM-DD')

                  const { blob, filename } = await getPayrollDownload(
                    row.id,
                    companyName,
                    date
                  )
                  triggerDownload(blob, filename)
                } catch (err: any) {
                  notification.error({
                    message: err?.message || 'Failed to download payroll',
                  })
                }
              }}
            >
              Download Contributions
            </Button>
          )}
          {row.assistedPayrollSyncResult.status ===
            'PENDING_PREVIOUS_RESULT' && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => openUploadResultModal(row.id)}
            >
              Upload Result
            </Button>
          )}
          {row.assistedPayrollSyncResult.status === 'QUEUED' && (
            <Button
              type="primary"
              loading={isProcessing}
              icon={<ThunderboltOutlined />}
              onClick={() => processPayroll(row.id)}
            >
              Process
            </Button>
          )}
        </Stack>
      ),
    },
  ]

  const totalNum = Number(total) || 0
  const pageNum = Number(page) || 1
  const pageSizeNum = Number(pageSize) || 10

  return (
    <Page
      title="Payroll"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: `/` },
            { title: 'HRIS', path: HRIS_PATH },
            { title: 'Payroll' },
          ]}
        />
      }
    >
      <Stack space={10}>
        <Typography size={16}>Initiate Process</Typography>
        <Stack direction="row" space={20} align="center">
          <Typography size={14}>Prepare Payroll for: </Typography>

          <RHFSelect
            name="clientPublicKey"
            control={control}
            options={companyList}
            optionFilterProp="label"
            placeholder="Select Company"
            showSearch
          />

          <RHFRangePicker name="payrollDates" control={control} />

          <Button
            variant="solid"
            color="primary"
            loading={isCreatingPayrollSync}
            onClick={handleSubmit((data) => {
              triggerPayrollSync(data)
              reset()
            })}
          >
            Prepare
          </Button>

          <ClientSelect
            allCompaniesPlacholder={{ id: '', companyName: 'All Companies' }}
            companies={companiesForSelect}
            defaultCompanyId=""
            selectedCompanyId={selectedCompanyId}
            onChange={(id: string) => setSelectedCompanyId(id)}
          />
        </Stack>

        <RowSection title="Assisted Manual - Payroll Activity">
          <Stack>
            <Table
              loading={isPending}
              dataSource={data as any}
              columns={columns as any}
              rowKey={(record) => record.id}
              pagination={{
                current: pageNum,
                pageSize: pageSizeNum,
                total: totalNum,
                onChange: (p?: number, ps?: number) => {
                  const validP =
                    typeof p === 'number' && Number.isFinite(p) && p > 0
                  const validPs =
                    typeof ps === 'number' && Number.isFinite(ps) && ps > 0
                  if (validPs && ps !== pageSize) {
                    setPageSize(ps)
                    setPage(1)
                    return
                  }
                  if (validP) {
                    setPage(p)
                  }
                },
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
              }}
              locale={{ emptyText: 'No Data' }}
            />
          </Stack>
        </RowSection>
      </Stack>
      {isDetailsModalOpen && (
        <PayrollDetailModal
          id={selectedPayrollId}
          isOpen={isDetailsModalOpen}
          onCancel={closeDetailsModal}
        />
      )}
      {isUploadResultModalOpen && (
        <UploadResultModal
          id={selectedPayrollId}
          isOpen={isUploadResultModalOpen}
          onCancel={closeUploadResultModal}
        />
      )}
    </Page>
  )
}
